import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import type {
  AjustarLogAcceso,
  AjustarLogAccesoRequest,
  JsModulo,
  LogAcceso,
} from './interfaces/auth.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/auth`;

  readonly sesion = signal<LogAcceso | null>(null);
  readonly moduloActual = computed(() =>
    this.sesion()?.modulosAbiertos.find((m) => m.cveModulo === environment.cveModulo),
  );

  getLogAcceso(): Observable<LogAcceso> {
    return this.http.post<LogAcceso>(`${this.base}/getLogAcceso`, {});
  }

  /**
   * Deja la sesión con este módulo abierto. Si se entró directo por URL estando en
   * otro módulo, entra con la organización de ese módulo; la API valida que la
   * organización tenga contratado este.
   */
  entrarAlModulo(): Observable<LogAcceso> {
    return this.getLogAcceso().pipe(
      switchMap((sesion) =>
        this.entrarA(sesion, environment.cveModulo, sesion.modulosAbiertos[0]?.organizacionId),
      ),
      tap((sesion) => this.sesion.set(sesion)),
    );
  }

  /**
   * Entra a otro módulo con la organización de Comité, sin cerrar este: la API de comités
   * pide que la sesión haya entrado al módulo del comité que se consulta.
   */
  abrirModulo(cveModulo: string): Observable<LogAcceso> {
    const sesion = this.sesion();
    if (!sesion) return throwError(() => new Error('Sin sesión'));
    return this.entrarA(sesion, cveModulo, this.moduloActual()?.organizacionId, true).pipe(
      tap((s) => this.sesion.set(s)),
    );
  }

  getJsModulos(organizacionId: number): Observable<JsModulo[]> {
    return this.http.post<JsModulo[]>(`${this.base}/getJsModulos`, { organizacionId });
  }

  // mismaOrganizacion: si el módulo quedó abierto con otra organización, se vuelve a entrar.
  private entrarA(
    sesion: LogAcceso,
    cveModulo: string,
    organizacionId: number | undefined,
    mismaOrganizacion = false,
  ): Observable<LogAcceso> {
    const abierto = sesion.modulosAbiertos.some(
      (m) =>
        m.cveModulo === cveModulo && (!mismaOrganizacion || m.organizacionId === organizacionId),
    );
    if (abierto) return of(sesion);
    if (!organizacionId) return throwError(() => new Error('Sin organización'));

    return this.getJsModulos(organizacionId).pipe(
      map((modulos) => modulos.find((m) => m.cve === cveModulo)),
      switchMap((modulo) =>
        modulo
          ? this.ajustarLogAcceso({ status: 'Login', moduloId: modulo.moduloId, organizacionId })
          : throwError(() => new Error('La organización no tiene este módulo')),
      ),
      switchMap(() => this.getLogAcceso()),
    );
  }

  ajustarLogAcceso(datos: AjustarLogAccesoRequest): Observable<AjustarLogAcceso> {
    return this.http.post<AjustarLogAcceso>(`${this.base}/ajustarLogAcceso`, datos);
  }

  // replace() para que el botón "atrás" no regrese a una pantalla sin sesión.
  irAlLogin(): void {
    this.sesion.set(null);
    window.location.replace(environment.loginUrl);
  }
}
